import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from '@/components/ui/ScrollToTop';
import CookieConsent from '@/components/ui/CookieConsent';
import PostHogPageviewTracker from '@/components/analytics/PostHogPageviewTracker';

// Layouts
import PublicLayout from '@/components/layout/PublicLayout';
import AdminLayout from '@/components/layout/AdminLayout';

// Pages
import Home from '@/pages/Home';
import Agenda from '@/pages/Agenda';
import EventDetail from '@/pages/EventDetail';
import About from '@/pages/About';
import Photos from '@/pages/Photos';
import Contact from '@/pages/Contact';
import PrivacyPolicy from '@/pages/PrivacyPolicy';

// Admin Pages
import Login from '@/pages/admin/Login';
import Dashboard from '@/pages/admin/Dashboard';
import AdminEvents from '@/pages/admin/AdminEvents';
import EventForm from '@/pages/admin/EventForm';
import AdminGallery from '@/pages/admin/AdminGallery';
import AlbumForm from '@/pages/admin/AlbumForm';
import AdminMessages from '@/pages/admin/AdminMessages';
import Settings from '@/pages/admin/Settings';
import Analytics from '@/pages/admin/Analytics';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <PostHogPageviewTracker />
            <Routes>
              {/* Public Routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/agenda" element={<Agenda />} />
                <Route path="/agenda/:slug" element={<EventDetail />} />
                <Route path="/a-propos" element={<About />} />
                <Route path="/photos" element={<Photos />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
              </Route>

              {/* Admin Routes */}
              <Route path="/admin/login" element={<Login />} />
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<Dashboard />} />
                <Route path="/admin/events" element={<AdminEvents />} />
                <Route path="/admin/events/new" element={<EventForm />} />
                <Route path="/admin/events/:id/edit" element={<EventForm />} />
                <Route path="/admin/gallery" element={<AdminGallery />} />
                <Route path="/admin/gallery/new" element={<AlbumForm />} />
                <Route path="/admin/gallery/:id" element={<AlbumForm />} />
                <Route path="/admin/messages" element={<AdminMessages />} />
                <Route path="/admin/analytics" element={<Analytics />} />
                <Route path="/admin/settings" element={<Settings />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CookieConsent />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
